import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useLang } from "../lib/i18n";
import { IMAGES, SLUG_TO_KEY, PRODUCT_ORDER } from "../lib/images";
import { Reveal } from "../lib/motion";
import { Overline, Btn } from "../components/ui-vision";

export default function ProductDetail() {
  const { slug } = useParams();
  const { t } = useLang();
  const key = SLUG_TO_KEY[slug];
  if (!key) return <Navigate to="/produits" replace />;

  const p = t.products[key];
  const media = IMAGES.products[key];
  const idx = PRODUCT_ORDER.findIndex((x) => x.key === key);
  const next = PRODUCT_ORDER[(idx + 1) % PRODUCT_ORDER.length];

  return (
    <article data-testid="product-detail">
      <header className="pt-28 md:pt-32 pb-10 md:pb-16 max-w-[1440px] mx-auto px-6 md:px-12">
        <Link to="/produits" className="line-hover inline-flex items-center gap-2 text-sm text-white/50 hover:text-white mb-10">
          <ArrowLeft size={16} /> {t.nav.products}
        </Link>
        <Overline className="mb-6">{p.overline}</Overline>
        <div className="grid lg:grid-cols-12 gap-8 items-end">
          <h1 className="lg:col-span-7 font-display text-5xl md:text-7xl font-medium tracking-tight leading-[0.95]">
            {p.name}
          </h1>
          <p className="lg:col-span-5 text-lg font-light text-white/60 leading-relaxed">{p.intro}</p>
        </div>
      </header>

      <div className="w-full max-w-[1440px] mx-auto px-6 md:px-12">
        <Reveal>
          <div className="overflow-hidden rounded-[14px] h-[55vh] md:h-[80vh]">
            <img src={media.hero} alt={p.name} className="w-full h-full object-cover" />
          </div>
        </Reveal>
      </div>

      <section className="py-24 md:py-32 max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-4">
            <p className="text-xs uppercase tracking-[0.25em] text-accent-yellow">{p.tagline}</p>
          </Reveal>
          <div className="lg:col-span-8 divide-y divide-white/10 border-t border-white/10">
            {p.features.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="grid grid-cols-12 gap-6 py-8 md:py-12 group">
                  <span className="col-span-2 font-display text-2xl md:text-3xl text-white/25 group-hover:text-accent-yellow transition-colors duration-500">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <h3 className="col-span-10 md:col-span-4 font-display text-2xl md:text-3xl font-medium">{f.t}</h3>
                  <p className="col-span-12 md:col-span-6 text-white/55 font-light leading-relaxed">{f.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="pb-24 md:pb-32 max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="grid md:grid-cols-2 gap-6 md:gap-8">
          {media.gallery.map((src, i) => (
            <Reveal key={i} delay={i * 0.05} className={i === 0 ? "md:col-span-2" : ""}>
              <div className={`overflow-hidden rounded-[14px] ${i === 0 ? "h-[50vh] md:h-[70vh]" : "h-[40vh] md:h-[55vh]"}`}>
                <img src={src} alt="" className="w-full h-full object-cover transition-transform duration-[900ms] hover:scale-105" />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="pb-24 md:pb-40 max-w-[1440px] mx-auto px-6 md:px-12">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-8 border-t border-white/10 pt-12">
          <Btn to="/rendez-vous" variant="solid" testid="product-quote-cta">
            {t.nav.quote}
          </Btn>
          <Link
            to={`/produits/${next.slug}`}
            data-testid="product-next"
            className="group text-right"
          >
            <span className="block text-xs uppercase tracking-[0.25em] text-white/40 mb-1">→</span>
            <span className="font-display text-2xl md:text-3xl group-hover:text-accent-yellow transition-colors duration-300">
              {t.products[next.key].name}
            </span>
          </Link>
        </div>
      </section>
    </article>
  );
}
